import { useMutation, useQueryClient } from '@tanstack/react-query';
import { queryKeys } from '@utils/consts';
import { useShowErrorToast } from '@utils/formHelpers';
import { DefaultApiResp, InventoryLocation } from '@root/globalTypes';
import { queryFetch } from '@services/api';
import { InventoryCreateRoutePointReq } from './types';
import { useQueryParams } from '@hooks/queryParams';

export const useCreateInventoryRoutePoint = ({
  location: { locationId, locationType },
  callback,
}: {
  location: InventoryLocation;
  callback: (data: DefaultApiResp) => void;
}) => {
  const {
    params: { routeId },
    parseParams,
  } = useQueryParams();

  const queryParams = parseParams({ routeId, locationId, locationType });

  const queryClient = useQueryClient();
  const { showErrorToast } = useShowErrorToast();
  const { mutate, isPending } = useMutation({
    mutationFn: (data: InventoryCreateRoutePointReq): Promise<DefaultApiResp> =>
      queryFetch({
        url: 'routes/points',
        data,
        opt: { method: 'POST' },
      }),
    onError: (err) => {
      console.error(err);
    },
    onSuccess: async ({ success }) => {
      if (!success) return;
      await queryClient.refetchQueries({
        queryKey: [...queryKeys.inventoryAvailable, queryParams],
      });
      await queryClient.refetchQueries({
        queryKey: [...queryKeys.inventoryRequested, queryParams],
      });
      await queryClient.refetchQueries({ queryKey: [...queryKeys.inventoryRoutes, routeId] });
      await queryClient.refetchQueries({ queryKey: queryKeys.dropdowns.routes });
      await queryClient.refetchQueries({
        queryKey: [...queryKeys.inventoryRemainingLoad, routeId],
      });
    },
    onSettled: (data) => {
      const { success, errors } = data as DefaultApiResp;
      if (errors) showErrorToast(Object.values(errors));
      if (success) callback(data as DefaultApiResp);
    },
  });

  return {
    mutate,
    isLoading: isPending,
  };
};
